import { computed, ref } from 'vue';

/**
 * Represents the tracking state for a single item
 */
export type ChangeTrackingState<TId, TData> = {
  id: TId;
  original: TData;
  current: TData;
  isDirty: boolean;
};

/**
 * A highly scalable and optimized composable for tracking changes to data items.
 * Uses dirty flags, field-level comparison, and a version counter for reactivity.
 *
 * @template TId - Type of the item identifier (e.g., number, string)
 * @template TData - Type of the data being tracked (e.g., { visibility?: SharingType; presetId?: number | null; })
 *
 * @example
 * ```ts
 * type PartnerSharingData = {
 *   visibility?: SharingType;
 *   presetId?: number | null;
 * };
 *
 * const changeTracking = useSharingHubChangeTracking<number, PartnerSharingData>();
 *
 * // Initialize tracking
 * changeTracking.initialize(partnerId, {
 *   visibility: SHARING,
 *   presetId: 123,
 * });
 *
 * // Update current state
 * changeTracking.updateCurrent(partnerId, { visibility: HIDDEN });
 *
 * // Check for changes
 * if (changeTracking.hasChanges(partnerId)) {
 *   const changes = changeTracking.getChanges(partnerId);
 *   console.log('Changed fields:', changes);
 * }
 *
 * // Get all changed items
 * const changedIds = changeTracking.getAllChangedIds();
 *
 * // Commit or reset changes
 * changeTracking.commitChanges(); // Make current the new original
 * changeTracking.reset(); // Revert current back to original
 * ```
 */
export default function useSharingHubChangeTracking<
  TId extends string | number,
  TData extends Record<string, unknown>,
>() {
  // Use Map for O(1) lookups
  const trackedItems = ref<Map<TId, ChangeTrackingState<TId, TData>>>(
    new Map(),
  );

  // Simple version counter for reactivity - increments on any change
  const version = ref(0);

  // Memoized results cache
  let cachedChangedIds: TId[] | null = null;
  let cachedChangedItems: ChangeTrackingState<TId, TData>[] | null = null;
  let cacheInvalidated = false;

  function invalidateCache() {
    cachedChangedIds = null;
    cachedChangedItems = null;
    cacheInvalidated = true;
    version.value++;
  }

  /**
   * Initialize tracking for an item
   */
  function initialize(id: TId, data: TData) {
    const state = {
      id,
      original: { ...data },
      current: { ...data },
      isDirty: false,
    };
    trackedItems.value.set(
      id,
      // @ts-expect-error - UnwrapRef type mismatch with generic constraints
      state as unknown as ChangeTrackingState<TId, TData>,
    );
    invalidateCache();
  }

  /**
   * Check if an item is being tracked
   */
  function isTracking(id: TId): boolean {
    return trackedItems.value.has(id);
  }

  /**
   * Update the current state of an item (marks as potentially dirty)
   */
  function updateCurrent(id: TId, data: Partial<TData>) {
    const state = trackedItems.value.get(id);
    if (!state) return;

    state.current = { ...state.current, ...data };
    state.isDirty = hasFieldChanges(
      state.original as TData,
      state.current as TData,
    );

    invalidateCache();
  }

  /**
   * Field-level comparison: Only compares specific fields that changed
   */
  function hasFieldChanges(original: TData, current: TData): boolean {
    const keys = new Set([...Object.keys(original), ...Object.keys(current)]);

    for (const key of keys) {
      if (original[key] !== current[key]) {
        return true;
      }
    }
    return false;
  }

  /**
   * Check if an item has changes (uses dirty flag + field-level comparison)
   */
  function hasChanges(id: TId): boolean {
    const state = trackedItems.value.get(id);
    if (!state) return false;

    // Quick check: if not marked dirty, definitely no changes
    if (!state.isDirty) return false;

    // Perform field-level comparison only if dirty
    return hasFieldChanges(state.original as TData, state.current as TData);
  }

  /**
   * Get only the changed fields for an item (returns partial object)
   */
  function getChanges(id: TId): Partial<TData> | null {
    const state = trackedItems.value.get(id);
    if (!state) return null;

    const changes: Partial<TData> = {};
    const keys = new Set([
      ...Object.keys(state.original),
      ...Object.keys(state.current),
    ]);

    for (const key of keys) {
      if (state.original[key] !== state.current[key]) {
        changes[key as keyof TData] = state.current[key] as TData[keyof TData];
      }
    }

    return Object.keys(changes).length > 0 ? changes : null;
  }

  /**
   * Get all IDs that have changes (memoized for performance)
   */
  function getAllChangedIds(): TId[] {
    if (cachedChangedIds && !cacheInvalidated) {
      return cachedChangedIds;
    }

    const changedIds: TId[] = [];
    for (const [id, state] of trackedItems.value) {
      if (
        state.isDirty &&
        hasFieldChanges(state.original as TData, state.current as TData)
      ) {
        changedIds.push(id);
      }
    }

    cachedChangedIds = changedIds;
    cacheInvalidated = false;
    return changedIds;
  }

  /**
   * Get all changed items with their full state (memoized for performance)
   */
  function getAllChangedItems(): ChangeTrackingState<TId, TData>[] {
    if (cachedChangedItems && !cacheInvalidated) {
      return cachedChangedItems;
    }

    const changedItems: ChangeTrackingState<TId, TData>[] = [];
    for (const state of trackedItems.value.values()) {
      if (
        state.isDirty &&
        hasFieldChanges(state.original as TData, state.current as TData)
      ) {
        changedItems.push(state as ChangeTrackingState<TId, TData>);
      }
    }

    cachedChangedItems = changedItems;
    cacheInvalidated = false;
    return changedItems;
  }

  /**
   * Reset changes for an item (or all items if no ID provided)
   * Reverts current back to original
   */
  function reset(id?: TId) {
    if (id !== undefined) {
      const state = trackedItems.value.get(id);
      if (state) {
        state.current = { ...state.original };
        state.isDirty = false;
      }
    } else {
      for (const state of trackedItems.value.values()) {
        state.current = { ...state.original };
        state.isDirty = false;
      }
    }
    invalidateCache();
  }

  /**
   * Commit changes for an item (or all items if no ID provided)
   * Makes current the new original (persists changes)
   */
  function commitChanges(id?: TId) {
    if (id !== undefined) {
      const state = trackedItems.value.get(id);
      if (state) {
        state.original = { ...state.current };
        state.isDirty = false;
      }
    } else {
      for (const state of trackedItems.value.values()) {
        state.original = { ...state.current };
        state.isDirty = false;
      }
    }
    invalidateCache();
  }

  /**
   * Batch update multiple items efficiently
   */
  function batchUpdate(updates: { id: TId; data: Partial<TData> }[]) {
    for (const update of updates) {
      const state = trackedItems.value.get(update.id);
      if (state) {
        state.current = { ...state.current, ...update.data };
        state.isDirty = hasFieldChanges(
          state.original as TData,
          state.current as TData,
        );
      }
    }
    invalidateCache();
  }

  /**
   * Cleanup: Remove tracking for items not in the provided set
   * IMPORTANT: Items with changes are preserved to prevent data loss
   */
  function cleanup(currentVisibleIds: Set<TId>) {
    const idsToRemove: TId[] = [];
    for (const [id, state] of trackedItems.value) {
      if (!currentVisibleIds.has(id) && !state.isDirty) {
        idsToRemove.push(id);
      }
    }

    for (const id of idsToRemove) {
      trackedItems.value.delete(id);
    }

    if (idsToRemove.length > 0) {
      invalidateCache();
    }
  }

  /**
   * Get the total number of tracked items
   */
  function getTrackingCount(): number {
    return trackedItems.value.size;
  }

  /**
   * Computed: Count of items with changes
   */
  const changedCount = computed(() => {
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    version.value; // Reactive dependency
    return getAllChangedIds().length;
  });

  /**
   * Computed: Whether any items have changes
   */
  const hasAnyChanges = computed(() => changedCount.value > 0);

  return {
    // Core operations
    initialize,
    isTracking,
    updateCurrent,
    hasChanges,
    getChanges,

    // Batch operations
    getAllChangedIds,
    getAllChangedItems,
    reset,
    commitChanges,
    batchUpdate,

    // Utility
    cleanup,
    getTrackingCount,

    // Computed properties
    changedCount,
    hasAnyChanges,
  };
}
